/**
 * Analytics utility to initialize Google Analytics (gtag) and track page views
 * Works only in production environment and when user has accepted analytics cookies
 */

import { isCookieTypeEnabled } from './cookie';

/**
 * Initialize Google Analytics script (production only, and with user consent)
 */
export const initializeGA = () => {
  // Only load GA in production
  if (process.env.NODE_ENV !== 'production' || !process.env.REACT_APP_GA_ID) {
    console.log('[Analytics] GA initialization skipped (dev mode or GA_ID not set)');
    return;
  }

  // Check if user has enabled analytics cookies
  if (!isCookieTypeEnabled('analytics')) {
    console.log('[Analytics] GA initialization skipped (analytics cookies not enabled)');
    return;
  }

  // Load gtag script dynamically
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${process.env.REACT_APP_GA_ID}`;
  document.head.appendChild(script);

  // Initialize dataLayer and gtag function
  window.dataLayer = window.dataLayer || [];
  function gtag() {
    window.dataLayer.push(arguments);
  }
  window.gtag = gtag;
  gtag('js', new Date());
  gtag('config', process.env.REACT_APP_GA_ID);

  console.log(`[Analytics] GA initialized with ID: ${process.env.REACT_APP_GA_ID}`);
};

/**
 * Track page view event for specific sections
 * @param {string} section - The section identifier (e.g., 'hero', 'about', 'services', 'contact')
 * @param {string} title - Optional page title to send to GA
 */
export const trackPageView = (section, title = '') => {
  // Only track in production if gtag is available
  if (process.env.NODE_ENV !== 'production' || !window.gtag) {
    console.log(`[Analytics] Page view skipped for section: ${section} (dev mode or gtag not initialized)`);
    return;
  }
  // Build a sensible title: prefer provided title, otherwise use
  // "{company} | {SectionLabel}" where company is taken from the
  // current document.title (left of any '|') and SectionLabel is
  // a capitalized section name.
  const company = (document.title && document.title.split('|')[0].trim()) || '';
  const sectionLabel = section.charAt(0).toUpperCase() + section.slice(1);
  const sectionTitle = title || (company ? `${company} | ${sectionLabel}` : `${sectionLabel}`);

  // Use a distinct path for each section (e.g. /hero, /about)
  const pagePath = `/${section}`;

  window.gtag('config', process.env.REACT_APP_GA_ID, {
    page_path: pagePath,
    page_title: sectionTitle,
  });

  window.gtag('event', 'page_view', {
    page_path: pagePath,
    page_title: sectionTitle,
  });

  console.log(`[Analytics] Page view tracked for section: ${section} -> ${pagePath} (${sectionTitle})`);
};

/**
 * Track custom events
 * @param {string} eventName - Name of the event
 * @param {object} eventData - Optional event parameters
 */
export const trackEvent = (eventName, eventData = {}) => {
  if (process.env.NODE_ENV !== 'production' || !window.gtag) {
    console.log(`[Analytics] Event skipped: ${eventName} (dev mode or gtag not initialized)`);
    return;
  }

  window.gtag('event', eventName, eventData);
  console.log(`[Analytics] Event tracked: ${eventName}`, eventData);
};

/**
 * Detect hash changes and auto-track page views for sections
 * Call this function once on app init to enable automatic section tracking
 */
export const setupHashChangeTracking = () => {
  if (process.env.NODE_ENV !== 'production') {
    console.log('[Analytics] Hash change tracking skipped (dev mode)');
    return;
  }

  // Check if analytics cookies are enabled
  if (!isCookieTypeEnabled('analytics')) {
    console.log('[Analytics] Hash change tracking skipped (analytics cookies not enabled)');
    return;
  }

  // List of sections to track
  const TRACKED_SECTIONS = ['hero', 'about', 'services', 'contact'];

  const handleHashChange = () => {
    const hash = window.location.hash.slice(1); // Remove '#'
    if (hash && TRACKED_SECTIONS.includes(hash)) {
      // Update document title to include section (company extracted from existing title)
      try {
        const company = (document.title && document.title.split('|')[0].trim()) || '';
        const labels = {
          hero: 'Home',
          about: 'About',
          services: 'Services',
          contact: 'Contact',
        };
        const label = labels[hash] || (hash.charAt(0).toUpperCase() + hash.slice(1));
        if (company) document.title = `${company} | ${label}`;
      } catch (e) {
        // ignore
      }
      trackPageView(hash);
    }
  };

  // Track initial hash on load
  handleHashChange();

  // Listen for hash changes
  window.addEventListener('hashchange', handleHashChange);

  console.log('[Analytics] Hash change tracking enabled');
};
